import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { getMasteryBand } from './masteryBands';
interface SubjectModuleRowProps {
  variant: 'parent' | 'teacher';
  moduleId: string;
  moduleTitle: string;
  moduleIcon: React.ReactNode;
  mastery: number;
  trend: 'up' | 'down' | 'stable';
  trendDelta?: number;
  distribution?: {
    strong: number;
    solid: number;
    developing: number;
    attention: number;
    total: number;
  };
  practicedCount?: number;
  parentSubtitle?: string;
  isExpanded: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}
export function SubjectModuleRow({
  variant,
  moduleId,
  moduleTitle,
  moduleIcon,
  mastery,
  trend,
  trendDelta,
  distribution,
  practicedCount,
  parentSubtitle,
  isExpanded,
  onToggle,
  children
}: SubjectModuleRowProps) {
  const band = getMasteryBand(mastery);
  return <div className="bg-white border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-id="element-8606">
      <div className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4 cursor-pointer" onClick={onToggle} role="button" tabIndex={0} aria-expanded={isExpanded} onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onToggle();
      }
    }} data-id="element-8607">
        {/* Icon */}
        <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${band.bgClass} ${band.textClass}`} data-id="element-8608">
          {moduleIcon}
        </div>

        {/* Title & Subtitle */}
        <div className="flex-1 min-w-0" data-id="element-8609">
          <div className="font-bold text-slate-900 truncate" data-id="element-8610">{moduleTitle}</div>
          <div className="text-xs font-medium text-slate-500 mt-0.5" data-id="element-8611">
            {variant === 'parent' ? <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-id="element-8612">
                <span className={`font-bold ${band.textClass}`} data-id="element-8613">
                  {band.label}
                </span>
                <span className="text-slate-300" data-id="element-8614">·</span>
                <span data-id="element-8615">{mastery}%</span>
                {parentSubtitle && <>
                    <span className="text-slate-300" data-id="element-8616">·</span>
                    <span className="text-slate-400" data-id="element-8617">{parentSubtitle}</span>
                  </>}
              </span> : <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5" data-id="element-8618">
                <span data-id="element-8619">{practicedCount || 0} leerlingen</span>
                <span className="text-slate-300" data-id="element-8620">·</span>
                <span className={`font-bold ${band.textClass}`} data-id="element-8621">
                  {mastery}% gemiddeld
                </span>
              </span>}
          </div>
        </div>

        {/* Progress / Distribution */}
        <div className="w-20 sm:w-28 md:w-32 flex-shrink-0 flex items-center justify-end" data-id="element-8622">
          {variant === 'parent' ? <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-8623">
              <motion.div initial={{
            width: 0
          }} animate={{
            width: `${mastery}%`
          }} className={`h-full rounded-full ${band.barClass}`} data-id="element-8624" />
            </div> : distribution && distribution.total > 0 ? <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden flex" data-id="element-8625">
              {distribution.strong > 0 && <div className="h-full bg-emerald-500" style={{
            width: `${distribution.strong / distribution.total * 100}%`
          }} title={`${distribution.strong} sterk`} data-id="element-8626" />}
              {distribution.solid > 0 && <div className="h-full bg-indigo-500" style={{
            width: `${distribution.solid / distribution.total * 100}%`
          }} title={`${distribution.solid} goed op weg`} data-id="element-8627" />}
              {distribution.developing > 0 && <div className="h-full bg-amber-500" style={{
            width: `${distribution.developing / distribution.total * 100}%`
          }} title={`${distribution.developing} aan het groeien`} data-id="element-8628" />}
              {distribution.attention > 0 && <div className="h-full bg-rose-500" style={{
            width: `${distribution.attention / distribution.total * 100}%`
          }} title={`${distribution.attention} aandacht nodig`} data-id="element-8629" />}
            </div> : <div className="w-16 h-2 bg-slate-100 rounded-full" data-id="element-8630" />}
        </div>

        {/* Trend */}
        <div className="hidden sm:flex w-16 flex-shrink-0 justify-end" data-id="element-8631">
          {trend === 'up' && <span className="flex items-center gap-0.5 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-8632">
              <ArrowUpRight className="w-3 h-3" data-id="element-8633" />
              {trendDelta && trendDelta > 0 ? `+${trendDelta}%` : ''}
            </span>}
          {trend === 'down' && <span className="flex items-center gap-0.5 text-[10px] font-bold text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded uppercase tracking-wider" data-id="element-8634">
              <ArrowDownRight className="w-3 h-3" data-id="element-8635" />
              {trendDelta && trendDelta < 0 ? `${trendDelta}%` : ''}
            </span>}
        </div>

        {/* Chevron */}
        <div className="w-6 flex-shrink-0 flex justify-end text-slate-400" data-id="element-8636">
          {isExpanded ? <ChevronUp className="w-5 h-5" data-id="element-8637" /> : <ChevronDown className="w-5 h-5" data-id="element-8638" />}
        </div>
      </div>

      <AnimatePresence data-id="element-8639">
        {isExpanded && children && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-8640">
            {children}
          </motion.div>}
      </AnimatePresence>
    </div>;
}